import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import decodeJson from '@seccon/shared/utils/decodeJson';
import ApiError from '@seccon/shared/utils/ApiError';
import dayjs from 'dayjs';

import { pick } from '@seccon/shared/utils/objectUtils';
import { formatPhone } from '@seccon/shared/utils/format';

/**
 * Fetches the foreign travel information
 * @param {string} foreignTravelId - The ID of the foreign travel entry
 * @param {string} origin - The origin of the request, either EMPLOYEE_PORTAL or FACILITIES
 * @returns {Promise<object>} - Foreign travel information
 */
function fetchForeignTravel (foreignTravelId, origin) {
  return fetch(`/foreignTravel/${ foreignTravelId }?origin=${ origin }`)
    .then(decodeJson)
    .catch(({ body, status }) => {
      if (status === 404) {
        throw new ApiError('Unable to find foreign travel information');
      }
      throw new ApiError(
        body ? `Error: ${ body }` : 'Unable to retrieve foreign travel information. Please reload the page and try again'
      );
    });
}

function convertBooleanToYesNoNA (value) {
  if (value === null || typeof value === 'undefined') {
    return 'n/a';
  }

  return value ? 'yes' : 'no';
}

/**
 * Sets the form state based on the foreign travel data retrieved
 * @param {object} data - foreign travel data
 * @param {Function} reset form state reset callback function
 */
function setFormState (data, reset) {
  const currentFormInfo = {
    // Spread the existing data
    ...data,
    foreignTravel: {
      ...data.foreignTravel,
      // Convert to dayjs
      departureDate: dayjs(data.foreignTravel.departureDate),
      // Convert to dayjs
      returnDate: dayjs(data.foreignTravel.returnDate),
      // Format the phone number
      foreignPhoneNumber: formatPhone(data.foreignTravel.foreignPhoneNumber),
      // Convert boolean values
      willUseForeignPassport: convertBooleanToYesNoNA(data.foreignTravel.willUseForeignPassport),
      destinations: data.foreignTravel.destinations.map((destination) => ({
        ...destination,
        // Convert startDate string to dayjs object
        startDate: dayjs(destination.startDate),
        // Convert endDate string to dayjs object
        endDate: dayjs(destination.endDate)
      })),
      travelingPersonnel: {
        ...data.foreignTravel.travelingPersonnel,
        // Format the phone number
        phoneCell: formatPhone(data.foreignTravel.travelingPersonnel.phoneCell),
        // Set the id and fullName of the traveling personnel
        id: pick(data.foreignTravel.travelingPersonnel, ['id', 'fullName']),
        passport: {
          ...data.foreignTravel.travelingPersonnel.passport,
          // Convert to dayjs
          expirationDate: dayjs(data.foreignTravel.travelingPersonnel.passport.expirationDate),
          // Convert to dayjs
          issueDate: dayjs(data.foreignTravel.travelingPersonnel.passport.issueDate)
        }
      },
      emergencyContacts: data.foreignTravel.emergencyContacts.map((contact) => ({
        ...contact,
        // Format the phone number
        phoneHome: formatPhone(contact.phoneHome),
        // Format the phone number
        phoneWork: formatPhone(contact.phoneWork),
        // Format the phone number
        phoneMobile: formatPhone(contact.phoneMobile)
      })),
      documents: [
        ...data.foreignTravel.documents.map((entry) => new File([], entry.filename, { type: 'application/custom' }))
      ]
    }
  };

  if (data.incident) {
    currentFormInfo.incident = {
      ...data.incident,
      // Convert boolean values
      compromise: convertBooleanToYesNoNA(data.incident.compromise),
      cognizantSecurityAuthority: data.incident.cognizantSecurityAuthority?.office,
      cognizantOfficeNotified: convertBooleanToYesNoNA(data.incident.cognizantOfficeNotified),
      // Convert to dayjs
      incidentDate: data.incident.incidentDate ? dayjs(data.incident.incidentDate) : null,
      initialReportDate: data.incident.initialReportDate ? dayjs(data.incident.initialReportDate) : null,
      finalReportDate: data.incident.finalReportDate ? dayjs(data.incident.finalReportDate) : null
    }
  }

  reset(currentFormInfo);
}

/**
 * Custom hook to get foreign travel information using React Query
 * @param {string} foreignTravelId - The ID of the foreign travel entry
 * @param {string} origin - The origin of the request, either EMPLOYEE_PORTAL or FACILITIES
 * @returns {object} - React Query result for foreign travel information
 */
const useForeignTravel = (foreignTravelId, origin, reset) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['foreignTravel', foreignTravelId, origin],
    queryFn: () => fetchForeignTravel(foreignTravelId, origin),
    // Prevents query from running if foreignTravelId or origin is not provided
    enabled: !!foreignTravelId && !!origin,
    refetchOnWindowFocus: false,
    staleTime: Infinity
  });

  useEffect(() => {
    if (data) {
      setFormState(data, reset);
    }
  }, [data]);

  return { data, isLoading, isError };
};

export default useForeignTravel;
